import { useRef, useState, type FormEvent } from 'react';
import { changePasswordSchema } from '@otakotakdev/shared';
import { useChangePassword } from '@/hooks/api';
import { ApiError } from '@/lib/api';

/**
 * Settings → Security → Change Password.
 *
 * Client validation is UX-only (the server re-validates and is
 * authoritative). Passwords live only in component state: they are never
 * logged, stored in localStorage/sessionStorage, or put in URLs.
 */

type FieldKey = 'currentPassword' | 'newPassword' | 'confirmPassword';
type FieldErrors = Partial<Record<FieldKey, string>>;

interface PasswordFieldProps {
  id: string;
  label: string;
  autoComplete: 'current-password' | 'new-password';
  value: string;
  error?: string;
  visible: boolean;
  onChange: (value: string) => void;
  onToggle: () => void;
}

function PasswordField({
  id,
  label,
  autoComplete,
  value,
  error,
  visible,
  onChange,
  onToggle,
}: PasswordFieldProps) {
  const showLabel = `Show ${label.toLowerCase()}`;
  const hideLabel = `Hide ${label.toLowerCase()}`;
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          className="input"
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <button
          type="button"
          className="btn-ghost shrink-0 px-2"
          onClick={onToggle}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const EMPTY: FormValues = { currentPassword: '', newPassword: '', confirmPassword: '' };

/** Client-side validation (UX only — the backend repeats all of this). */
function validateClient(v: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  const parsed = changePasswordSchema.safeParse({
    currentPassword: v.currentPassword,
    newPassword: v.newPassword,
    confirmPassword: v.confirmPassword,
  });
  if (!parsed.success) {
    const fieldErrors = parsed.error.flatten().fieldErrors;
    if (fieldErrors.currentPassword?.[0]) errors.currentPassword = fieldErrors.currentPassword[0];
    if (fieldErrors.newPassword?.[0]) errors.newPassword = fieldErrors.newPassword[0];
    if (fieldErrors.confirmPassword?.[0]) errors.confirmPassword = fieldErrors.confirmPassword[0];
  }
  if (!errors.newPassword && !errors.confirmPassword && v.newPassword !== v.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }
  if (
    !errors.newPassword &&
    !errors.currentPassword &&
    v.newPassword &&
    v.newPassword === v.currentPassword
  ) {
    errors.newPassword = 'New password must be different from the current password.';
  }
  return errors;
}

/** Map a server/network error to a field or form-level message. */
function mapServerError(err: unknown): {
  expired?: boolean;
  errors?: FieldErrors;
  message?: string;
} {
  if (!(err instanceof ApiError)) {
    // fetch() rejection — network failure, never expose internals.
    return { message: 'Could not reach the server. Check your connection and try again.' };
  }
  switch (err.code) {
    case 'UNAUTHENTICATED':
      return { expired: true };
    case 'INVALID_CURRENT_PASSWORD':
      return { errors: { currentPassword: err.message } };
    case 'PASSWORD_REUSE_NOT_ALLOWED':
      return { errors: { newPassword: err.message } };
    case 'VALIDATION_ERROR': {
      const fieldErrors = (err.details?.fieldErrors ?? {}) as Record<string, string[]>;
      const mapped: FieldErrors = {};
      if (fieldErrors.currentPassword?.[0]) mapped.currentPassword = fieldErrors.currentPassword[0];
      if (fieldErrors.newPassword?.[0]) mapped.newPassword = fieldErrors.newPassword[0];
      if (fieldErrors.confirmPassword?.[0]) mapped.confirmPassword = fieldErrors.confirmPassword[0];
      if (Object.keys(mapped).length > 0) return { errors: mapped };
      return { message: err.message };
    }
    case 'RATE_LIMITED':
      return { message: err.message };
    default:
      return { message: 'Something went wrong. Please try again.' };
  }
}

export function ChangePasswordForm() {
  const changePassword = useChangePassword();
  const [values, setValues] = useState<FormValues>(EMPTY);
  const [visible, setVisible] = useState<Record<FieldKey, boolean>>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [success, setSuccess] = useState(false);
  // Ref guard: blocks a same-tick double submit before React state flushes.
  const inFlightRef = useRef(false);

  const pending = changePassword.isPending;

  const setValue = (key: FieldKey, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    // Clear stale errors as the user edits.
    setFieldErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    setFormError(null);
    setSuccess(false);
    setExpired(false);
  };

  const toggleVisible = (key: FieldKey) => {
    setVisible((v) => ({ ...v, [key]: !v[key] }));
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Guard against duplicate submissions while a request is in flight.
    if (pending || inFlightRef.current) return;

    setFieldErrors({});
    setFormError(null);
    setExpired(false);
    setSuccess(false);

    const clientErrors = validateClient(values);
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }

    inFlightRef.current = true;
    changePassword.mutate(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      },
      {
        onSuccess: () => {
          inFlightRef.current = false;
          setValues(EMPTY);
          setVisible({ currentPassword: false, newPassword: false, confirmPassword: false });
          setSuccess(true);
        },
        onError: (err) => {
          inFlightRef.current = false;
          const mapped = mapServerError(err);
          if (mapped.expired) setExpired(true);
          if (mapped.errors) setFieldErrors(mapped.errors);
          if (mapped.message) setFormError(mapped.message);
        },
      },
    );
  };

  return (
    <section className="card p-4" aria-labelledby="security-heading">
      <h2 id="security-heading" className="mb-1 text-sm font-semibold text-muted">
        Security
      </h2>
      <p className="mb-4 text-sm text-muted">Change password</p>

      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {expired && (
          <div
            role="alert"
            className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
          >
            Your session has expired.{' '}
            <a href="/login" className="underline">
              Sign in again
            </a>
          </div>
        )}
        {success && (
          <div
            role="status"
            className="rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-sm text-success"
          >
            Password changed successfully.
          </div>
        )}
        {formError && (
          <div role="alert" className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {formError}
          </div>
        )}

        <PasswordField
          id="current-password"
          label="Current password"
          autoComplete="current-password"
          value={values.currentPassword}
          error={fieldErrors.currentPassword}
          visible={visible.currentPassword}
          onChange={(v) => setValue('currentPassword', v)}
          onToggle={() => toggleVisible('currentPassword')}
        />
        <PasswordField
          id="new-password"
          label="New password"
          autoComplete="new-password"
          value={values.newPassword}
          error={fieldErrors.newPassword}
          visible={visible.newPassword}
          onChange={(v) => setValue('newPassword', v)}
          onToggle={() => toggleVisible('newPassword')}
        />
        <PasswordField
          id="confirm-password"
          label="Confirm new password"
          autoComplete="new-password"
          value={values.confirmPassword}
          error={fieldErrors.confirmPassword}
          visible={visible.confirmPassword}
          onChange={(v) => setValue('confirmPassword', v)}
          onToggle={() => toggleVisible('confirmPassword')}
        />

        <p className="text-xs text-muted">
          At least 10 characters. Passphrases and spaces are welcome.
        </p>

        <div>
          <button type="submit" className="btn-primary" disabled={pending}>
            {pending ? 'Changing…' : 'Change Password'}
          </button>
        </div>
      </form>
    </section>
  );
}
